import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Badge, EmptyState } from "../components/kr";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, CheckCircle, XCircle,
  CaretRight, ChartBar, Receipt,
} from "@phosphor-icons/react";
import { Pagination, usePagination } from "../components/Pagination";

export default function Parties({ kind }) {
  const label = kind === "customer" ? "Customers" : "Suppliers";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [history, setHistory] = useState({});  // { partyId: {loading, data} }
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();

  const load = async () => setItems((await api.get("/parties", { params: { kind } })).data);
  useEffect(() => { load(); }, [kind]);

  const del = async (id, name) => {
    const ok = await confirm({
      title: `Delete ${kind}?`,
      message: `"${name}" will be removed from the master. Existing invoices & accounting entries are kept.`,
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/parties/${id}`);
    load();
  };

  const toggleExpand = async (p) => {
    const isOpen = !!expanded[p.id];
    setExpanded(s => ({ ...s, [p.id]: !isOpen }));
    // Only fetch history for customers (suppliers don't have sales)
    if (!isOpen && kind === "customer" && !history[p.id]) {
      setHistory(h => ({ ...h, [p.id]: { loading: true } }));
      try {
        const r = await api.get(`/parties/${p.id}/stock-history`);
        setHistory(h => ({ ...h, [p.id]: { loading: false, data: r.data } }));
      } catch (e) {
        setHistory(h => ({ ...h, [p.id]: { loading: false, error: e.response?.data?.detail || "Failed" } }));
      }
    }
  };

  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  useEffect(() => { setPage(1); }, [kind, setPage]);
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label={kind === "customer" ? "MASTER LIST" : "VENDOR MASTER"}
        title={label}
        subtitle={kind === "customer"
          ? "Customers who buy from you. Click a row to view their stock/order history."
          : "Suppliers you buy from. Reused in purchase orders & invoices."}
        action={<Btn onClick={() => setOpen("new")} data-testid={`add-${kind}`}><Plus size={14} /> Add {kind === "customer" ? "Customer" : "Supplier"}</Btn>}
      />

      <Card className="!p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title={`No ${label.toLowerCase()}`} /></div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-50">
              {isAdmin && (
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    checked={ms.selected.length > 0 && ms.selected.length === items.length}
                    onChange={() => ms.toggleAll(items.map(x => x.id))}
                    className="accent-orange-600"
                  />
                </div>
              )}
              <div className={isAdmin ? "col-span-2" : "col-span-3"}>Name</div>
              <div className="col-span-2">GSTIN</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-2">State</div>
              <div className="col-span-2">{kind === "customer" ? "They Owe You" : "You Owe Them"}</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {paged.map(i => {
              const isOpen = !!expanded[i.id];
              const canExpand = kind === "customer";
              return (
                <div key={i.id} data-testid={`party-row-${i.id}`}>
                  <button
                    type="button"
                    onClick={() => canExpand && toggleExpand(i)}
                    className={`w-full grid grid-cols-12 gap-3 items-center px-4 py-3 text-left ${canExpand ? "hover:bg-zinc-50 cursor-pointer" : ""}`}
                  >
                    {isAdmin && (
                      <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={ms.isSelected(i.id)}
                          onChange={() => ms.toggle(i.id)}
                          className="accent-orange-600"
                          data-testid={`party-check-${i.id}`}
                        />
                      </div>
                    )}
                    <div className={`${isAdmin ? "col-span-2" : "col-span-3"} flex items-center gap-2`}>
                      {canExpand && <CaretRight size={14} weight="bold" className={`text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />}
                      <span className="font-bold">{i.name}</span>
                    </div>
                    <div className="col-span-2 font-mono text-xs flex items-center gap-1">
                      {i.gstin || "—"}
                      {i.gstin && (i.gstin_valid
                        ? <CheckCircle size={12} weight="fill" className="text-green-600" />
                        : <XCircle size={12} weight="fill" className="text-red-600" />)}
                    </div>
                    <div className="col-span-2 font-mono text-xs">{i.phone || "—"}</div>
                    <div className="col-span-2 text-xs">{i.state || "—"}</div>
                    <div className="col-span-2 font-mono">
                      {i.outstanding > 0 ? (
                        <div>
                          <span className={`font-bold ${kind === "customer" ? "text-red-700" : "text-orange-700"}`}>
                            ₹{i.outstanding.toLocaleString("en-IN")}
                          </span>
                          <div className="text-[10px] text-zinc-500">
                            {i.outstanding_docs} pending{i.oldest_due ? ` · oldest ${i.oldest_due}` : ""}
                          </div>
                          {kind === "customer" && (
                            <div className="text-[10px] font-bold text-orange-700 mt-0.5">Credit: {i.credit_days ?? 15}d</div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="text-green-700 text-xs">Clear</span>
                          {kind === "customer" && (
                            <div className="text-[10px] font-bold text-orange-700 mt-0.5">Credit: {i.credit_days ?? 15}d</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end gap-2">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setOpen(i); }}
                        className="text-orange-600 hover:text-orange-800"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); del(i.id, i.name); }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash size={14} weight="bold" />
                      </span>
                    </div>
                  </button>

                  {isOpen && canExpand && (
                    <div className="px-6 pb-5 bg-zinc-50/60 border-t border-zinc-200" data-testid={`party-history-${i.id}`}>
                      <StockHistory state={history[i.id]} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={items.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="party-pager"
        />
      </Card>

      {open && <PartyModal party={open === "new" ? null : open} kind={kind}
        onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="parties"
        label={kind === "customer" ? "customers" : "suppliers"}
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function StockHistory({ state }) {
  if (!state) return null;
  if (state.loading) return <div className="p-4 text-xs font-mono text-zinc-500">Loading history…</div>;
  if (state.error) return <div className="p-4 text-xs text-red-700">{state.error}</div>;
  const d = state.data;
  if (!d) return null;
  if (d.total_invoices === 0) {
    return (
      <div className="py-4 text-sm text-zinc-500 italic">
        No sales invoices yet for this customer.
      </div>
    );
  }
  return (
    <div className="pt-4">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatBlock label="Total Invoices" value={d.total_invoices} icon={Receipt} />
        <StatBlock label="Total Business" value={`₹${d.total_value.toLocaleString("en-IN")}`} icon={ChartBar} tone="text-green-700" />
        <StatBlock label="Products Bought" value={d.products.length} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Top Products</div>
          <div className="bg-white border border-zinc-200">
            <table className="kr-table !mb-0">
              <thead><tr><th>Product</th><th>Qty</th><th>Revenue</th><th>Last</th></tr></thead>
              <tbody>
                {d.products.slice(0, 8).map((p, idx) => (
                  <tr key={idx}>
                    <td className="text-xs">{p.description}</td>
                    <td className="font-mono text-xs">{p.qty} {p.unit}</td>
                    <td className="font-mono text-xs font-bold">₹{p.revenue.toLocaleString("en-IN")}</td>
                    <td className="font-mono text-[10px] text-zinc-500">{p.last_ordered || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Recent Invoices</div>
          <div className="bg-white border border-zinc-200">
            <table className="kr-table !mb-0">
              <thead><tr><th>Doc#</th><th>Date</th><th>Total</th><th>Balance</th></tr></thead>
              <tbody>
                {d.invoices.slice(0, 8).map(inv => (
                  <tr key={inv.id}>
                    <td className="font-mono text-xs">{inv.doc_no}</td>
                    <td className="font-mono text-xs">{inv.doc_date}</td>
                    <td className="font-mono text-xs font-bold">₹{inv.grand_total.toLocaleString("en-IN")}</td>
                    <td className="font-mono text-xs text-red-700">₹{(inv.balance_due || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, icon: Icon, tone = "text-zinc-900" }) {
  return (
    <div className="bg-white border border-zinc-200 p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon size={14} className="text-zinc-400" />}
      </div>
      <div className={`font-mono font-black text-lg mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function PartyModal({ party, kind, onClose, onSaved }) {
  const isNew = !party;
  const [f, setF] = useState(party || {
    name: "", gstin: "", address: "", phone: "", email: "", state: "", notes: "",
    credit_days: 15, default_discount_percent: 0,
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, kind };
      if (kind === "customer") {
        body.credit_days = parseInt(body.credit_days || 15);
        body.default_discount_percent = parseFloat(body.default_discount_percent || 0) || 0;
      }
      if (isNew) await api.post("/parties", body);
      else await api.put(`/parties/${party.id}`, body);
      toast.success(isNew ? "Added" : "Updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? `New ${kind}` : `Edit · ${party.name}`} size="lg">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="party-form">
        <Field label="Name" full><Input value={f.name} onChange={e => upd("name", e.target.value)} required data-testid="party-name" /></Field>
        <Field label="GSTIN"><Input value={f.gstin} onChange={e => upd("gstin", e.target.value.toUpperCase())} data-testid="party-gstin" /></Field>
        <Field label="State"><Input value={f.state} onChange={e => upd("state", e.target.value)} data-testid="party-state" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={e => upd("phone", e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={e => upd("email", e.target.value)} /></Field>
        <Field label="Address" full><Input value={f.address} onChange={e => upd("address", e.target.value)} /></Field>
        {kind === "customer" && (
          <Field label="Credit Cycle (days)">
            <Input type="number" value={f.credit_days ?? 15} onChange={e => upd("credit_days", parseInt(e.target.value || 15))} data-testid="party-credit-days" />
          </Field>
        )}
        {kind === "customer" && (
          <Field label="Default Discount %">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={f.default_discount_percent ?? 0}
              onChange={e => upd("default_discount_percent", e.target.value)}
              placeholder="e.g. 5"
              data-testid="party-default-discount"
            />
          </Field>
        )}
        <Field label="Notes" full><Input value={f.notes} onChange={e => upd("notes", e.target.value)} /></Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="party-submit">{isNew ? "Add" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
}
